import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { photos } from '../data/photos';
import { achievements } from '../data/achievements';
import './Home.css';

const Home = () => {
  const [imageErrors, setImageErrors] = useState(new Set());
  const featuredPhotos = photos.slice(0, 3);
  const recentAchievements = achievements.slice(0, 3);

  const handleImageError = (imageId) => {
    setImageErrors(prev => new Set(prev).add(imageId));
  };

  // For hero image, we can use the first achievement or create a dedicated hero image
  const heroImageUrl = "/images/hero/hero-image.jpg";

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Capturing Precious Moments
            <span className="hero-subtitle">Children's Fashion Photography</span>
          </h1>
          <p className="hero-description">
            Professional photography specializing in children's fashion and modeling. 
            Creating beautiful memories that last a lifetime.
          </p>
          <div className="hero-buttons">
            <Link to="/gallery" className="btn btn-primary">
              View Gallery
            </Link>
            <Link to="/achievements" className="btn btn-secondary">
              See Achievements
            </Link>
          </div>
        </div>
        <div className="hero-image">
          {heroImageUrl && !imageErrors.has('hero') ? (
            <img 
              src={heroImageUrl} 
              alt="Featured Photography"
              className="hero-img"
              onError={() => handleImageError('hero')}
            />
          ) : (
            <div className="hero-image-placeholder">
              <span>Featured Photo</span>
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>About Nicole</h2>
              <p>
                With over a decade of experience in children's fashion photography, 
                Nicole specializes in capturing the unique personality and style of 
                young models. Her work has been featured in prestigious magazines 
                and has earned recognition from industry professionals.
              </p>
              <p>
                Each photograph is carefully crafted to showcase not just the 
                clothing, but the child's natural beauty and confidence. From casual 
                everyday wear to elegant formal attire, Nicole's photography brings 
                out the best in every child.
              </p>
              <Link to="/about" className="btn btn-primary">
                Learn More
              </Link>
            </div>
            <div className="about-stats">
              <div className="stat">
                <h3>100+</h3>
                <p>Photographs</p>
              </div>
              <div className="stat">
                <h3>15+</h3>
                <p>Magazine Features</p>
              </div>
              <div className="stat">
                <h3>8</h3>
                <p>Industry Awards</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Photos */}
      <section className="featured-photos">
        <div className="container">
          <h2>Featured Work</h2>
          <div className="photos-grid">
            {featuredPhotos.map((photo) => (
              <div key={photo.id} className="photo-card">
                {photo.imageUrl && !imageErrors.has(`photo-${photo.id}`) ? (
                  <img 
                    src={photo.imageUrl} 
                    alt={photo.title}
                    className="photo-card-image"
                    onError={() => handleImageError(`photo-${photo.id}`)}
                  />
                ) : (
                  <div className="photo-image-placeholder">
                    <span>{photo.title}</span>
                  </div>
                )}
                <div className="photo-info">
                  <h3>{photo.title}</h3>
                  <p className="photo-date">{new Date(photo.imageDate).toLocaleDateString()}</p>
                  <p className="photo-vendors">Vendors: {photo.vendors.join(', ')}</p>
                  <p className="photo-description">{photo.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="view-all-container">
            <Link to="/gallery" className="btn btn-secondary">
              View All Photos
            </Link>
          </div>
        </div>
      </section>

      {/* Recent Achievements */}
      <section className="achievements-section">
        <div className="container">
          <h2>Recent Achievements</h2>
          <div className="achievements-grid">
            {recentAchievements.map((achievement) => (
              <div key={achievement.id} className="achievement-card">
                {achievement.imageUrl && !imageErrors.has(`achievement-${achievement.id}`) ? (
                  <img 
                    src={achievement.imageUrl} 
                    alt={achievement.title}
                    className="achievement-card-image"
                    onError={() => handleImageError(`achievement-${achievement.id}`)}
                  />
                ) : (
                  <div className="achievement-image-placeholder">
                    <span>{achievement.type}</span>
                  </div>
                )}
                <div className="achievement-content">
                  <h3>{achievement.title}</h3>
                  <p className="achievement-date">
                    {new Date(achievement.date).toLocaleDateString()}
                  </p>
                  <p>{achievement.description}</p>
                  {achievement.link && (
                    <a 
                      href={achievement.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-ghost"
                    >
                      View Article
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="view-all-container">
            <Link to="/achievements" className="btn btn-secondary">
              View All Achievements
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <h2>Services</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">📸</div>
              <h3>Fashion Photography</h3>
              <p>Professional children's fashion photography for clothing brands and catalogs.</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🎭</div>
              <h3>Modeling Portfolios</h3>
              <p>Create stunning modeling portfolios for young talent and aspiring models.</p>
            </div>
            <div className="service-card">
              <div className="service-icon">👗</div>
              <h3>Product Photography</h3>
              <p>High-quality product photography showcasing children's clothing and accessories.</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🌟</div>
              <h3>Special Events</h3>
              <p>Capture special moments at fashion shows, events, and promotional activities.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
